import { useDroppable } from "@dnd-kit/core";
import type { Admin, OutreachProspect, OutreachStage } from "@lumika/shared";
import { cn } from "@lumika/ui";
import { ProspectCard } from "./ProspectCard";

type Props = {
  stage: OutreachStage;
  label: string;
  prospects: OutreachProspect[];
  ownerNames: Map<string, string>;
  admins: Admin[];
  onCardClick: (prospect: OutreachProspect) => void;
  onAssign: (prospectId: string, ownerAdminId: string | null) => void;
};

/**
 * One droppable column in the Kanban board. Renders a fixed-height
 * scrollable card list so tall columns don't overflow the board row.
 */
export function BoardColumn({
  stage,
  label,
  prospects,
  ownerNames,
  admins,
  onCardClick,
  onAssign,
}: Props) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });

  return (
    <section
      className="flex h-full min-h-0 w-64 shrink-0 flex-col gap-2 md:w-72"
      aria-label={`${label} column`}
    >
      {/* Column header */}
      <div className="flex shrink-0 items-center justify-between px-1">
        <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
          {label}
        </h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {prospects.length}
        </span>
      </div>

      {/* Drop target + card list */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-xl border p-2 transition-colors",
          isOver ? "border-primary/50 bg-primary/5" : "border-border/60 bg-muted/30",
        )}
      >
        {prospects.length === 0 ? (
          <div className="flex flex-1 items-center justify-center px-2 py-8 text-center text-xs text-muted-foreground/50">
            No prospects
          </div>
        ) : (
          prospects.map((p) => (
            <ProspectCard
              key={p.id}
              prospect={p}
              ownerName={p.ownerAdminId ? (ownerNames.get(p.ownerAdminId) ?? null) : null}
              admins={admins}
              onClick={onCardClick}
              onAssign={onAssign}
            />
          ))
        )}
      </div>
    </section>
  );
}
